import { createContext, useContext, useEffect, useMemo, useState } from "react";

const LanguageContext = createContext(null);

const STRINGS = {
  en: {
    nav: { stay: "Stay", experiences: "Experiences", nearby: "Nearby", dining: "Dining", cricket: "Cricket Field", gallery: "Gallery", contact: "Contact", book: "Reserve Stay", admin: "Admin" },
    hero: {
      eyebrow: "Naljuri Inn · Valley of Meghalaya",
      title_1: "Experience the Serenity of Naljuri",
      title_2: "Valley of Meghalaya.",
      sub: "An aristocratic eco-retreat at the threshold of Jaflong, the Sadapathor crystal waters, and the misted ridges of the Khasi hills.",
      cta_book: "Reserve Your Stay",
      cta_explore: "Check Availability",
      scroll: "Scroll to discover",
      live: "Live River Status",
      river_clear: "Crystal Clear",
    },
    booking: {
      title: "Reserve your nights",
      sub: "Direct inquiry. Our concierge replies on WhatsApp within minutes.",
      name: "Full name", phone: "Phone (WhatsApp)", checkin: "Arrival", checkout: "Departure",
      guests: "Guests", room: "Suite preference", message: "Notes for the concierge",
      submit: "Reserve Your Stay", select_room: "Any available suite",
    },
    welcome: {
      eyebrow: "A basecamp built for adventure",
      title: "Where the Sylhet wilderness meets quiet luxury.",
      body: "Naljuri Inn sits at the doorstep of Jaflong — a few minutes from the green-water Lalakhal river, Naljuri Falls, the Sreepur tea garden, and the misted Meghalaya ridges. We built this house for travellers who want their adventure to end in soft beds, hot showers, and a porch that listens to the forest.",
      pill_1: "Round-the-clock security", pill_2: "Uninterrupted power", pill_3: "Concierge service", pill_4: "Local cuisine",
    },
    experiences: {
      eyebrow: "Our market alphas",
      title: "Three reasons travellers cross the country for us.",
      sub: "Sadapathor's crystal water, Jaflong's stone-gathering culture, and the viral cricket field next door — chaperoned by our local guides.",
      items: {
        sadapathor: { title: "Sadapathor — Crystal Waters", desc: "A glass-clear basin where the river meets the marble. Living art, in real time." },
        jaflong_stone: { title: "Jaflong — Stone-Gathering Culture", desc: "Walk the river beds where Khasi stone-pickers harvest river-rounded jewels — a living craft." },
        cricket: { title: "Naljuri Viral Cricket Field", desc: "\"Banglar Pahalgam\" — the country's most photographed amateur ground, three minutes from your room." },
        jharna:   { title: "Naljuri Jharna", desc: "A monsoon waterfall hidden in our own forest. A 20-minute walk from the property, all to yourselves." },
        shapla:   { title: "Dibir Haor & Shapla Bil", desc: "Thousands of red water lilies under a quiet winter sunrise." },
      }
    },
    cricket: {
      eyebrow: "The viral landmark",
      title: "The Naljuri Cricket Field.",
      sub: "Bangladesh's most photographed amateur ground — a green clearing rimmed by the Khasi hills, three minutes from your room.",
      stats: [
        { n: "1.2M+", l: "Reels & posts" },
        { n: "360°", l: "Hill panorama" },
        { n: "5 min", l: "From the property" },
      ],
      cta: "Plan a sunrise visit",
    },
    sadapathor: {
      eyebrow: "Living art",
      title: "Crystal Waters of Sadapathor.",
      sub: "The white-stone basin where the Mahadeo river leaves the Meghalaya ridge. Walk between marble boulders, pebble-paint with your hands, and breathe air that tastes of rain.",
      tags: ["Stone-gathering heritage", "Turquoise basin", "Sunrise photography"],
      cta: "Add this to my stay",
    },
    rooms: {
      eyebrow: "The accommodation table",
      title: "Three quiet ways to spend the night.",
      sub: "Aristocratic interiors. Panoramic hill views. Concierge-led service.",
      per_night: "/ night", view: "Reserve this suite", capacity: "guests",
      offer_label: "AI Special Offer", standard: "Standard rate", booked_via_ai: "Limited-time digital booking benefit",
      featured_strip: "Most reserved this season",
    },
    amenities: { eyebrow: "On the property", title: "Everything you need, nothing you don't.",
      items: ["Uninterrupted power", "High-speed Wi-Fi", "On-site dining", "International basketball court", "Badminton court", "Forest swing garden", "Bonfire pit", "Family lounges"]
    },
    testimonials: { eyebrow: "Guest moments", title: "Notes from people who chose us." },
    gallery: { eyebrow: "Visual diary", title: "A few frames from the property and around.", more_fb: "More on Facebook" },
    contact: {
      eyebrow: "Find us", title: "Plan your visit.",
      address: "Naljuri, Jaflong, Jaintapur, Sylhet — 3156, Bangladesh",
      hotline: "Hotline", email_label: "Email", website: "Website", facebook: "Follow on Facebook",
      catalog: "View WhatsApp Catalog",
    },
    footer: { rights: "All rights reserved.", made: "Naljuri Inn · A nature-first basecamp" },
    weather: { now: "Sylhet now", loading: "Reading the valley" },
  },
  bn: {
    nav: { stay: "থাকা", experiences: "অভিজ্ঞতা", nearby: "আশেপাশে", dining: "খাবার", cricket: "ক্রিকেট মাঠ", gallery: "গ্যালারি", contact: "যোগাযোগ", book: "এখনই বুক করুন", admin: "অ্যাডমিন" },
    hero: {
      eyebrow: "নলজুরি ইন · মেঘালয়ের উপত্যকা",
      title_1: "নলজুরির প্রশান্তি অনুভব করুন",
      title_2: "মেঘালয়ের উপত্যকায়।",
      sub: "জাফলং, সাদাপাথরের স্বচ্ছ জল, এবং খাসি পাহাড়ের কোলে — এক অভিজাত ইকো-রিট্রিট।",
      cta_book: "এখনই রিজার্ভ করুন",
      cta_explore: "উপলব্ধতা দেখুন",
      scroll: "নিচে স্ক্রল করুন",
      live: "নদীর লাইভ অবস্থা",
      river_clear: "স্বচ্ছ পানি",
    },
    booking: {
      title: "আপনার রাত বুক করুন",
      sub: "সরাসরি ইনকোয়্যারি। আমাদের কনসিয়ার্জ হোয়াটসঅ্যাপে কয়েক মিনিটেই উত্তর দেবেন।",
      name: "পুরো নাম", phone: "ফোন (হোয়াটসঅ্যাপ)", checkin: "চেক-ইন", checkout: "চেক-আউট",
      guests: "অতিথি", room: "স্যুটের পছন্দ", message: "কনসিয়ার্জের জন্য নোট",
      submit: "এখনই রিজার্ভ করুন", select_room: "যেকোনো উপলব্ধ স্যুট",
    },
    welcome: {
      eyebrow: "অ্যাডভেঞ্চারের জন্য তৈরি বেসক্যাম্প",
      title: "যেখানে সিলেটের বন্যতা মিশেছে নিরিবিলি বিলাসিতায়।",
      body: "নলজুরি ইন জাফলং-এর দরজায় — সবুজ জলের লালাখাল নদী, নলজুরি ঝরনা, শ্রীপুর চা বাগান এবং মেঘালয়ের কুয়াশা ঢাকা পাহাড় থেকে কয়েক মিনিট দূরে। যারা চান তাদের অ্যাডভেঞ্চারের শেষটা হোক নরম বিছানা, গরম পানির গোসল আর বনের শব্দ শোনা একটি বারান্দায় — তাদের জন্যই এই বাড়ি।",
      pill_1: "২৪/৭ নিরাপত্তা", pill_2: "নিরবচ্ছিন্ন বিদ্যুৎ", pill_3: "কনসিয়ার্জ সেবা", pill_4: "স্থানীয় খাবার",
    },
    experiences: {
      eyebrow: "আমাদের বাজার-পার্থক্য",
      title: "যে তিন কারণে অতিথিরা সারাদেশ থেকে আসেন।",
      sub: "সাদাপাথরের স্বচ্ছ জল, জাফলং-এর পাথর-সংগ্রহের সংস্কৃতি, আর পাশের ভাইরাল ক্রিকেট মাঠ — আমাদের স্থানীয় গাইডদের সাথে।",
      items: {
        sadapathor: { title: "সাদাপাথর — স্বচ্ছ জলরাজ্য", desc: "কাচের মতো স্বচ্ছ জল আর মার্বেল পাথর — এক জীবন্ত শিল্পকর্ম।" },
        jaflong_stone: { title: "জাফলং — পাথর-সংগ্রহের সংস্কৃতি", desc: "নদীর তীরে খাসি পাথর-সংগ্রাহকদের জীবন্ত ঐতিহ্য।" },
        cricket: { title: "নলজুরি ভাইরাল ক্রিকেট মাঠ", desc: "\"বাংলার পেহেলগাম\" — দেশের সবচেয়ে বেশি ছবি তোলা অপেশাদার মাঠ, রুম থেকে তিন মিনিট।" },
        jharna:   { title: "নলজুরি ঝরনা", desc: "আমাদের নিজস্ব বনের ভেতর লুকানো এক বর্ষাকালীন ঝরনা। প্রপার্টি থেকে ২০ মিনিটের হাঁটাপথ — শুধু আপনাদের জন্য।" },
        shapla:   { title: "ডিবির হাওর ও শাপলা বিল", desc: "শীতের সূর্যোদয়ে হাজারো লাল শাপলার মেলা।" },
      }
    },
    cricket: {
      eyebrow: "ভাইরাল ল্যান্ডমার্ক",
      title: "নলজুরি ক্রিকেট মাঠ।",
      sub: "বাংলাদেশের সবচেয়ে বেশি ছবি তোলা অপেশাদার মাঠ — খাসি পাহাড়ে ঘেরা সবুজ মাঠ, আপনার রুম থেকে মাত্র তিন মিনিট।",
      stats: [
        { n: "১২ লাখ+", l: "রিল ও পোস্ট" },
        { n: "৩৬০°", l: "পাহাড়ের ভিউ" },
        { n: "৫ মিনিট", l: "প্রপার্টি থেকে" },
      ],
      cta: "সূর্যোদয়ের পরিকল্পনা করুন",
    },
    sadapathor: {
      eyebrow: "জীবন্ত শিল্প",
      title: "সাদাপাথরের স্বচ্ছ জলরাজ্য।",
      sub: "মেঘালয়ের পাহাড় থেকে নেমে আসা মহাদেও নদীর সাদা-পাথরের অববাহিকা। মার্বেল পাথরের ফাঁকে হাঁটুন, পাথর কুড়ান, এবং বৃষ্টির স্বাদ নেওয়া বাতাসে শ্বাস নিন।",
      tags: ["পাথর কুড়ানোর ঐতিহ্য", "টারকোয়েজ অববাহিকা", "সূর্যোদয়ের ফটোগ্রাফি"],
      cta: "এটা আমার ভ্রমণে যোগ করুন",
    },
    rooms: {
      eyebrow: "আবাসন তালিকা",
      title: "রাত কাটানোর তিনটি নিরিবিলি উপায়।",
      sub: "অভিজাত ইন্টেরিয়র। প্যানোরামিক পাহাড়ের ভিউ। কনসিয়ার্জ সেবা।",
      per_night: "/ রাত", view: "এই স্যুট রিজার্ভ করুন", capacity: "জন",
      offer_label: "এআই বিশেষ অফার", standard: "স্ট্যান্ডার্ড দাম", booked_via_ai: "সীমিত-সময় ডিজিটাল বুকিং সুবিধা",
      featured_strip: "এই মৌসুমের সবচেয়ে রিজার্ভড",
    },
    amenities: { eyebrow: "প্রপার্টিতে যা আছে", title: "যা যা দরকার, তার সবই — অপ্রয়োজনীয় কিছু নয়।",
      items: ["নিরবচ্ছিন্ন বিদ্যুৎ", "হাই-স্পিড Wi-Fi", "অন-সাইট ডাইনিং", "আন্তর্জাতিক মানের বাস্কেটবল কোর্ট", "ব্যাডমিন্টন কোর্ট", "ফরেস্ট সুইং গার্ডেন", "বনফায়ার পিট", "ফ্যামিলি লাউঞ্জ"]
    },
    testimonials: { eyebrow: "অতিথিদের মুহূর্ত", title: "যারা আমাদের বেছে নিয়েছেন তাদের কথা।" },
    gallery: { eyebrow: "দৃশ্য ডায়েরি", title: "প্রপার্টি ও আশেপাশের কয়েকটি ফ্রেম।", more_fb: "ফেসবুকে আরও দেখুন" },
    contact: {
      eyebrow: "আমাদের খুঁজুন", title: "আপনার ভ্রমণ পরিকল্পনা করুন।",
      address: "নলজুরি, জাফলং, জৈন্তাপুর, সিলেট — ৩১৫৬, বাংলাদেশ",
      hotline: "হটলাইন", email_label: "ইমেইল", website: "ওয়েবসাইট", facebook: "ফেসবুকে ফলো করুন",
      catalog: "হোয়াটসঅ্যাপ ক্যাটালগ দেখুন",
    },
    footer: { rights: "সর্বস্বত্ব সংরক্ষিত।", made: "নলজুরি ইন · প্রকৃতি-প্রথম বেসক্যাম্প" },
    weather: { now: "এখন সিলেট", loading: "উপত্যকা পড়া হচ্ছে" },
  }
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("naljuri_lang") || "en");

  useEffect(() => {
    localStorage.setItem("naljuri_lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.classList.toggle("lang-bn", lang === "bn");
  }, [lang]);

  const t = STRINGS[lang];
  const toggle = () => setLang((p) => (p === "en" ? "bn" : "en"));

  const value = useMemo(() => ({ lang, setLang, toggle, t }), [lang, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
